<template>
  <div class="search-wrapper" v-click-outside="closeSearch">
    <div class="search-input-box"
         :class="{ 'is-searching': loading, 'has-results': showResults && results.length > 0 }">
      <div class="inner-content">
        <v-icon small color="#666">mdi-magnify</v-icon>
        <input
                ref="searchInput"
                type="text"
                v-model="query"
                @input="onInput"
                @focus="showResults = true"
                :placeholder="$t('search')"
                autocomplete="off"
        />
        <div v-if="loading" class="loader-spinner"></div>
        <button v-else-if="query" @click="clearSearch" class="btn-clear">&times;</button>
      </div>
    </div>

    <transition name="dropdown-anim">
      <div v-if="showResults && query.length >= 3" class="search-dropdown">

        <div v-if="loading" class="info-box">Searching...</div>

        <div v-else-if="results.length === 0" class="info-box">
          No events found for "<strong>{{ query }}</strong>"
        </div>

        <div v-else class="results-layout">

          <div class="filters-section" v-if="uniqueSports.length > 0">
            <div class="filter-row">
              <span
                      class="filter-badge"
                      :class="{ 'is-active': !selectedSport }"
                      @click="toggleSport(null)"
              >All Sports</span>
              <span
                      v-for="sport in uniqueSports"
                      :key="'s-'+sport.id"
                      class="filter-badge"
                      :class="{ 'is-active': selectedSport === sport.id }"
                      @click="toggleSport(sport.id)"
              >{{ sport.name }}</span>
            </div>

            <div class="filter-row" v-if="uniqueTournaments.length > 1">
              <span
                      class="filter-badge"
                      :class="{ 'is-active': !selectedTournament }"
                      @click="toggleTournament(null)"
              >All Tournaments</span>
              <span
                      v-for="tour in uniqueTournaments"
                      :key="'t-'+tour.id"
                      class="filter-badge"
                      :class="{ 'is-active': selectedTournament === tour.id }"
                      @click="toggleTournament(tour.id)"
              >{{ tour.name }}</span>
            </div>
          </div>

          <div v-if="filteredResults.length === 0" class="info-box">
            No events match the selected filters.
          </div>

          <div v-else class="results-container">
            <div
                    v-for="event in filteredResults"
                    :key="event.eventId"
                    @click="selectEvent(event)"
                    class="event-card"
            >
              <div class="card-meta">
                <span class="badge-sport">{{ event.sportName }}</span>
                <span class="path-info">{{ event.categoryName }} • {{ event.tournamentName }}</span>
              </div>
              <div class="card-main">
                <span v-if="event.status === '2'" class="live-indicator"></span>
                <span class="match-name">{{ event.eventName }}</span>
                <span class="match-date">{{ formatDate(event.scheduledDate) }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import axios from 'axios';
  import _ from 'lodash';

  const SEARCH_MAX_DAYS = 16;

  export default {
    name: 'GlobalEventSearch',
    mounted() {
      this._focusHandler = () => {
        this.$nextTick(() => { this.$refs.searchInput && this.$refs.searchInput.focus(); });
      };
      this.$root.$on('focus_tree_search', this._focusHandler);
    },
    beforeDestroy() {
      this.$root.$off('focus_tree_search', this._focusHandler);
    },
    data() {
      return {
        query: '',
        results: [],
        loading: false,
        showResults: false,
        selectedSport: null,
        selectedTournament: null
      };
    },
    computed: {
      apiUrl() { return this.$store.getters['settings/getUrlApi']; },
      token() { return this.$store.getters['settings/getToken']; },
      lang() { return this.$store.getters['settings/getLanguage']; },
      liveMode() { return this.$store.getters['ui/getViewMode']; },

      currentSearchStatus() {
        return this.liveMode === 'detail' ? '2' : '1';
      },

      uniqueSports() {
        const sportsMap = new Map();
        this.results.forEach(event => {
          if (!sportsMap.has(event.sportId)) {
            sportsMap.set(event.sportId, { id: event.sportId, name: event.sportName });
          }
        });
        return Array.from(sportsMap.values());
      },

      uniqueTournaments() {
        const tourMap = new Map();
        this.results.forEach(event => {
          if (!this.selectedSport || event.sportId === this.selectedSport) {
            if (!tourMap.has(event.tournamentId)) {
              tourMap.set(event.tournamentId, { id: event.tournamentId, name: event.tournamentName });
            }
          }
        });
        return Array.from(tourMap.values());
      },

      filteredResults() {
        return this.results.filter(event => {
          const matchSport = this.selectedSport ? event.sportId === this.selectedSport : true;
          const matchTour = this.selectedTournament ? event.tournamentId === this.selectedTournament : true;
          return matchSport && matchTour;
        });
      }
    },
    methods: {
      onInput: _.debounce(function () {
        if (this.query.length < 3) {
          this.results = [];
          this.resetFilters();
          return;
        }
        this.fetchResults();
      }, 300),

      async fetchResults() {
        this.loading = true;
        try {
          const params = {
            q: this.query,
            provider: 'lsport',
            status: this.currentSearchStatus,
            type: "match",
            limit: 20,
            maxDays: SEARCH_MAX_DAYS
          };

          // Aggiungo la subscriptionType solo se sto cercando in modalità Live
          if (this.currentSearchStatus === '2') {
            params.subscriptionType = 1;
          }

          const config = {
            headers: { 'Authorization': `Bearer ${this.token}` },
            params: params
          };

          const response = await axios.get(`${this.apiUrl}/v3/fixtures/search`, config);
          this.results = response.data.data || [];
          this.resetFilters();
          this.showResults = true;
        } catch (error) {
          console.error("Search API Error:", error);
        } finally {
          this.loading = false;
        }
      },

      async selectEvent(event) {
        this.showResults = false;
        this.query = '';
        this.resetFilters();

        this.$store.dispatch('odds/setLoadingPreview', { status: true });

        const compHome = event.competitors && event.competitors[0] ? event.competitors[0].name : '';
        const compAway = event.competitors && event.competitors[1] ? event.competitors[1].name : '';

        const payload = {
          'source': event.eventId,
          'group': 0,
          'sport': event.sportId,
          'category': event.categoryId,
          'tournament': event.tournamentId,
          'status': event.status,
          'startEventTime': event.scheduledDate,
          'sportName': event.sportName,
          'categoryName': event.categoryName,
          'tournamentName': event.tournamentName,
          'competitorHome': compHome,
          'competitorAway': compAway,
          'competitorHomeId': event.competitors && event.competitors[0] ? event.competitors[0].id : undefined,
          'competitorAwayId': event.competitors && event.competitors[1] ? event.competitors[1].id : undefined,

        };

        if (window.innerWidth < 1200 || window.forceMobileView === 'true') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        try {
          if (this.currentSearchStatus === '2') {
            await this.$store.dispatch('detailLive/fetchEventLiveDetailV5', payload);
          } else {
            await this.$store.dispatch('detailPrematch/fetchEventDetailV3', payload);
          }
        } catch (err) {
          console.error("Navigation error:", err);
        } finally {
          this.$store.dispatch('odds/setLoadingPreview', { status: false });
        }
      },

      toggleSport(sportId) {
        this.selectedSport = sportId;
        this.selectedTournament = null;
      },

      toggleTournament(tournamentId) {
        this.selectedTournament = tournamentId;
      },

      resetFilters() {
        this.selectedSport = null;
        this.selectedTournament = null;
      },

      clearSearch() {
        this.query = '';
        this.results = [];
        this.resetFilters();
        this.showResults = false;
      },

      closeSearch() {
        this.showResults = false;
      },

      formatDate(dateStr) {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' }) + ' ' +
                d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    },
    directives: {
      'click-outside': {
        bind(el, binding, vnode) {
          el.clickOutsideEvent = function (event) {
            if (!(el === event.target || el.contains(event.target))) {
              vnode.context[binding.expression](event);
            }
          };
          document.body.addEventListener('click', el.clickOutsideEvent);
        },
        unbind(el) {
          document.body.removeEventListener('click', el.clickOutsideEvent);
        }
      }
    }
  };
</script>

<style scoped>
  /* Base Wrapper */
  .search-wrapper {
    position: relative;
    width: 100%;
  }

  /* Input Styles */
  .search-input-box {
    background: var(--hsla-1)!important;

    border: 1px solid var(--border-color);
    border-radius: 25px;
    transition: border-color 0.3s;
  }

  .search-input-box.has-results {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-color: var(--border-color);
  }

  .inner-content {
    display: flex;
    align-items: center;
    padding: 8px 14px;
    gap: 4px;
  }
  .inner-content .v-icon {
    font-size: var(--font-size-10xl) !important;
    color: var(--text-platform)!important;
  }

  .icon-search {
    margin-right: 10px;
    font-size: var(--font-size-5xl);
    color: var(--text-op-50-platform);
  }

  input {
    background: transparent;
    border: none;
    color: var(--text-platform);
    font-size: var(--font-size-4xl);
    width: 100%;
    outline: none;
  }

  input::placeholder { color: var(--text-platform); font-weight: 700 }

  .btn-clear {
    background: none;
    border: none;
    color: var(--text-op-50-platform);
    cursor: pointer;
    font-size: var(--font-size-8xl);
    padding: 0 4px;
  }

  .loader-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid var(--color-primary);
    border-top-color: var(--color-primary-light);
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  /* Dropdown Container */
  .search-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-top: none;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.6);
    z-index: 9999;
    overflow: hidden;
  }

  /* Dynamic Filters Section */
  .filters-section {
    background: var(--bg-header);
    border-bottom: 1px solid var(--border-color);
    padding: 10px 12px;
  }

  .filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    padding-bottom: 6px;
    -webkit-overflow-scrolling: touch; /* Smooth scroll on iOS */
  }

  /* Elegant custom scrollbar for desktop usability */
  .filter-row::-webkit-scrollbar {
    height: 4px;
  }
  .filter-row::-webkit-scrollbar-track {
    background: #0f172a;
    border-radius: 4px;
  }
  .filter-row::-webkit-scrollbar-thumb {
    background: #334155;
    border-radius: 4px;
  }
  .filter-row::-webkit-scrollbar-thumb:hover {
    background: var(--bg-card-hover);
  }

  .filter-row + .filter-row {
    margin-top: 8px;
  }

  /* Filter Badges */
  .filter-badge {
    flex-shrink: 0; /* CRUCIAL: prevents chips from compressing and forces scroll */
    background: var(--bg-cell);
    color: var(--text-op-50-platform);
    font-size: var(--font-size-xl);
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid var(--border-color);
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
  }

  .filter-badge:hover {
    background: var(--bg-cell-hover);
    color: var(--text-platform);
  }

  .filter-badge.is-active {
    background: var(--color-primary);
    color: var(--text-platform);
    border-color: transparent;
  }

  /* Results Layout */
  .results-container {
    max-height: 350px;
    overflow-y: auto;
  }

  .info-box {
    padding: 25px;
    color: var(--text-op-50-platform);
    text-align: center;
    font-size: var(--font-size-4xl);
  }

  /* Event Cards */
  .event-card {
    padding: 12px 14px;
    border-bottom: 1px solid var(--border-color);
    cursor: pointer;
    background: var(--bg-card-gradient-to-r);
    transition: background-color 0.15s ease;
  }

  .event-card:hover { background: var(--bg-card); }

  .card-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  .badge-sport {
    background: var(--color-primary);
    color: var(--text-platform);
    font-size: var(--font-size-xs);
    padding: 2px 5px;
    border-radius: 4px;
    text-transform: uppercase;
    font-weight: bold;
  }

  .path-info {
    font-size: var(--font-size-xl);
    color: var(--text-op-50-platform);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .card-main {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
  }

  .live-indicator {
    display: inline-block;
    min-width: 8px;
    height: 8px;
    background-color: var(--live);
    border-radius: 50%;
    margin-right: 8px;
    animation: pulse 1.5s infinite;
  }

  .match-name {
    color: var(--text-platform);
    font-weight: 600;
    font-size: var(--font-size-3xl);
    margin-right: auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .match-date {
    font-size: var(--font-size-xl);
    color: var(--color-accent);
    margin-left: 10px;
    white-space: nowrap;
  }

  /* Animations */
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @keyframes pulse {
    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
    70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
  }

  .dropdown-anim-enter-active, .dropdown-anim-leave-active {
    transition: opacity 0.2s, transform 0.2s;
  }

  .dropdown-anim-enter, .dropdown-anim-leave-to {
    opacity: 0;
    transform: translateY(-5px);
  }

  @media (max-width: 600px) {
    .search-wrapper{
    }
    .search-input-box {
      background: var(--hsla-1)!important;
    }
  }
</style>